import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getAccessToken } from '../utils/auth';
import {
  getComparatorBenchmark,
  getRegimeHistory,
  getRingAccuracy,
  getRingTrend,
} from '../utils/httpApi';
import { EMPTY, fmtInt, fmtNum, fmtPercent } from '../utils/statsFormat';
import ChartCard from './charts/ChartCard';
import LineChart from './charts/LineChart';
import HeaderHint from './HeaderHint';
import DepartureAccuracyPanel from './DepartureAccuracyPanel';
import './charts/charts.css';
import './statistics/statistics.css';
import './PredictionAccuracy.css';

/**
 * How accurate the arrival predictions actually are, by how far out the aircraft was.
 *
 * Distinct from PredictionQualityChart, which bands by time-to-go over whole days. This
 * bands by DISTANCE RING and takes one observation per flight per ring — the moment it
 * crossed — so a slow long-haul leg that emits 200 rows counts exactly as much as a short
 * one that emits 30. That is what makes the long-range estimator (everything beyond the
 * 200 NM ring, where the ML model stops and the analytic estimator takes over) legible at
 * all.
 *
 * Cohorts grow as the rings shrink: far more aircraft reach 20 NM than are ever seen at
 * 1000 NM. The flight count is shown on every row for that reason, and a thin outer ring
 * is not hidden — it is the honest state of the measurement.
 */

const WINDOWS = [
  { hours: 2, label: '2h' },
  { hours: 8, label: '8h' },
  { hours: 24, label: '24h' },
];

/**
 * Benchmark windows. Wider than the ring windows because the cohort is far smaller: a
 * comparison needs the flight to have been linked to a published flight number AND to have
 * had an in-flight estimate captured before it landed.
 */
const BENCH_WINDOWS = [
  { hours: 24, label: '24h' },
  { hours: 72, label: '3d' },
  { hours: 168, label: '7d' },
];

/**
 * The only airport the comparator benchmark runs at, mirroring COMPARED_AIRPORTS in
 * PredictionStatsService. LPPR's schedule refreshes every three hours, so a comparison there
 * would measure the refresh interval rather than either estimator.
 */
const BENCHMARKED_AIRPORT = 'EIDW';

const TREND_RANGES = [
  { days: 7, label: '7d' },
  { days: 14, label: '14d' },
  { days: 30, label: '30d' },
  { days: 90, label: '90d' },
];

/**
 * The backend sends LocalDateTime, which carries no zone. The server runs UTC, so a bare
 * string must be read as UTC — `new Date(s)` would read it as the viewer's local time and
 * slide the whole chart by their offset.
 */
function utcMillis(s) {
  if (!s) return null;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(s);
  const t = Date.parse(hasZone ? s : `${s}Z`);
  return Number.isNaN(t) ? null : t;
}

function dayMillis(s) {
  if (!s) return null;
  const t = Date.parse(`${s}T00:00:00Z`);
  return Number.isNaN(t) ? null : t;
}

/** Signed minutes, always with a sign, so late and early never read alike. */
function fmtSigned(v) {
  if (v == null) return EMPTY;
  const n = fmtNum(Math.abs(v), 1);
  return `${v < 0 ? '−' : '+'}${n}`;
}

/**
 * Where the ML model stops (LandingPredictionService.MAX_PREDICTION_RANGE_NM) and the
 * analytic long-range estimator takes over. Everything on this page is organised around it.
 */
const HANDOVER_NM = 200;

/** Fewest flights a ring needs on a day before that day's MAE is plotted in the history. */
const MIN_TREND_FLIGHTS = 5;

/**
 * Is `a` the better (smaller) error of the pair? False when either is missing, so an empty
 * bucket never bolds one side by default.
 */
function leads(a, b) {
  return a != null && b != null && a < b;
}

/** Re-number a filtered series list so colours restart at slot 1 in each chart. */
function reslot(s, i) {
  return { ...s, slot: i + 1 };
}

function PredictionAccuracy({ airportIcao }) {
  const [windowHours, setWindowHours] = useState(24);
  const [trendDays, setTrendDays] = useState(30);

  const [benchHours, setBenchHours] = useState(72);

  const [rings, setRings] = useState(null);
  const [bench, setBench] = useState(null);
  const [trend, setTrend] = useState(null);
  const [regime, setRegime] = useState(null);

  const [loadingWindow, setLoadingWindow] = useState(false);
  const [loadingBench, setLoadingBench] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState(null);

  const loadWindow = useCallback(async () => {
    if (!airportIcao) return;
    setLoadingWindow(true);
    try {
      const token = getAccessToken();
      const res = await getRingAccuracy(airportIcao, windowHours, null, token);
      setRings(res && res.rings ? res : null);
      setError(null);
    } catch (e) {
      setError('Could not load ring accuracy.');
    } finally {
      setLoadingWindow(false);
    }
  }, [airportIcao, windowHours]);

  const loadHistory = useCallback(async () => {
    if (!airportIcao) return;
    setLoadingHistory(true);
    try {
      const token = getAccessToken();
      // The regime corrector refreshes every five minutes, so a 90-day request would be
      // ~26k points against a chart 600px wide. Cap it at the backend's own ceiling and
      // let the longer ranges speak through the daily trend instead.
      const regimeHours = Math.min(trendDays * 24, 720);
      const [t, r] = await Promise.all([
        getRingTrend(airportIcao, trendDays, null, token),
        getRegimeHistory(airportIcao, regimeHours, token),
      ]);
      setTrend(t && t.points ? t : null);
      setRegime(r && r.samples ? r : null);
    } catch (e) {
      setError('Could not load accuracy history.');
    } finally {
      setLoadingHistory(false);
    }
  }, [airportIcao, trendDays]);

  const loadBench = useCallback(async () => {
    if (!airportIcao) return;
    setLoadingBench(true);
    try {
      const token = getAccessToken();
      const res = await getComparatorBenchmark(airportIcao, benchHours, token);
      setBench(res && res.horizons ? res : null);
    } catch (e) {
      // Deliberately not raising the page-level error: the benchmark is one panel, and the
      // ring table above it is the page's job. A comparator outage should not blank both.
      setBench(null);
    } finally {
      setLoadingBench(false);
    }
  }, [airportIcao, benchHours]);

  useEffect(() => { loadBench(); }, [loadBench]);
  useEffect(() => { loadWindow(); }, [loadWindow]);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const ringRows = rings && rings.rings ? rings.rings : [];
  const scoredRings = ringRows.filter(r => r.flights > 0);

  const benchRows = bench && bench.horizons ? bench.horizons : [];
  const benchMatched = benchRows.reduce((n, h) => n + (h.matched || 0), 0);

  // One series per ring, so the reader can see the fan-out with range directly.
  const trendSeries = useMemo(() => {
    if (!trend || !trend.points || trend.points.length === 0) return [];
    const ringsNm = trend.ringsNm && trend.ringsNm.length
      ? trend.ringsNm
      : [...new Set(trend.points.map(p => p.ringNm))].sort((a, b) => a - b);

    return ringsNm.map((ringNm, i) => ({
      ringNm,
      name: `${ringNm} NM`,
      slot: i + 1,
      samples: trend.points
        // A day's mean over one or two flights is an anecdote, not a trend — at the
        // 200 NM handover ring that is all there ever is, and it plotted as a line
        // floating above every real one.
        .filter(p => p.ringNm === ringNm && p.maeMinutes != null
          && (p.flights == null || p.flights >= MIN_TREND_FLIGHTS))
        .map(p => ({ ts: dayMillis(p.date), v: p.maeMinutes }))
        .filter(p => p.ts != null)
        .sort((a, b) => a.ts - b.ts),
    })).filter(s => s.samples.length > 0);
  }, [trend]);

  /**
   * Split at the model handover rather than plotting all eight rings on one axis.
   *
   * They are not the same measurement and they are not the same order of magnitude: inside
   * 200 NM the conformal ML model runs and sits near 1.5-4 min, outside it the analytic
   * long-range estimator runs and swings to 35. Sharing a y-axis flattened every close-in
   * ring into the bottom few percent of the plot — the rings carrying almost all the traffic
   * became a single thick line along the floor.
   */
  const closeInSeries = useMemo(
    () => trendSeries.filter(s => s.ringNm <= HANDOVER_NM).map(reslot),
    [trendSeries]);
  const longRangeSeries = useMemo(
    () => trendSeries.filter(s => s.ringNm > HANDOVER_NM).map(reslot),
    [trendSeries]);

  const regimeSeries = useMemo(() => {
    if (!regime || !regime.samples || regime.samples.length === 0) return [];
    const applied = [];
    for (const s of regime.samples) {
      const ts = utcMillis(s.capturedAt);
      if (ts == null) continue;
      // A neutral period is a real, meaningful state - the corrector ran and decided it had
      // too little to go on - and it is plotted as an explicit zero rather than a gap, so a
      // flat stretch at zero cannot be mistaken for missing data.
      applied.push({ ts, v: s.applied ? s.offsetMinutes : 0 });
    }
    return applied.length ? [{ name: 'offset applied', slot: 1, samples: applied }] : [];
  }, [regime]);

  const spreadSeries = useMemo(() => {
    if (!regime || !regime.samples) return [];
    const pts = regime.samples
      .filter(s => s.spreadScale != null && s.applied)
      .map(s => ({ ts: utcMillis(s.capturedAt), v: s.spreadScale }))
      .filter(p => p.ts != null);
    return pts.length ? [{ name: 'spread scale', slot: 2, samples: pts }] : [];
  }, [regime]);

  const flightsSeries = useMemo(() => {
    if (!regime || !regime.samples) return [];
    const pts = regime.samples
      .map(s => ({ ts: utcMillis(s.capturedAt), v: s.flightCount }))
      .filter(p => p.ts != null);
    return pts.length ? [{ name: 'landed flights in window', slot: 3, samples: pts }] : [];
  }, [regime]);

  const clamp = regime && regime.clampMinutes ? regime.clampMinutes : 12;
  const neutralCount = regime && regime.samples
    ? regime.samples.filter(s => !s.applied).length
    : 0;

  return (
    <div className="pred-accuracy">
      {error && <div className="pred-accuracy-error">{error}</div>}

      <div className="pred-accuracy-controls">
        <span className="pred-accuracy-label">Landed in the last</span>
        <div className="predictions-tabs pred-accuracy-pills">
          {WINDOWS.map(w => (
            <button
              key={w.hours}
              className={`predictions-tab${windowHours === w.hours ? ' active' : ''}`}
              onClick={() => setWindowHours(w.hours)}
            >{w.label}</button>
          ))}
        </div>
        <span className="pred-accuracy-note">
          {rings && rings.modelVersion ? `model ${rings.modelVersion}` : ''}
        </span>
      </div>

      <div className="stats-group-title">Accuracy by range ring</div>
      <p className="pred-accuracy-lead">
        Every flight that landed in the window, scored once at each distance ring it crossed on
        the way in — so a long-haul leg emitting hundreds of predictions counts exactly as much
        as a short one. Error is measured against our own touchdown time, in minutes, from the
        moment the prediction was made.
      </p>
      <div className="pred-accuracy-table-wrap">
        <table className="predictions-table pred-accuracy-table">
          <thead>
            <tr>
              <th>Ring<HeaderHint text="Distance from the airport at which each flight is scored. Each flight counts once per ring it crossed, using the prediction we made as it crossed." /></th>
              <th className="numeric">Flights<HeaderHint text="Flights that crossed this ring and landed in the window. Small counts swing the percentages: at 25 flights, one flight is 4 points." /></th>
              <th className="numeric">MAE<HeaderHint text="Mean absolute error in minutes: how far, on average, our predicted landing was from the actual touchdown, ignoring direction." /></th>
              <th className="numeric">Bias<HeaderHint text="Signed mean error in minutes. Positive = we predicted too late (it landed earlier than we said); negative = too early." /></th>
              <th className="numeric">p50<HeaderHint text="Median absolute error: half of flights were closer than this, half further." /></th>
              <th className="numeric">p90<HeaderHint text="90th percentile absolute error: 9 in 10 flights were within this many minutes." /></th>
              <th className="numeric">p95<HeaderHint text="95th percentile absolute error — the bad-day figure. The long-range constants were tuned on this, not on MAE." /></th>
              <th className="numeric">&lt;5 min<HeaderHint text="Share of flights whose actual touchdown was within ±5 minutes of our prediction. A fixed window, the same width for every flight." /></th>
              <th className="numeric">&lt;10 min<HeaderHint text="Share of flights whose actual touchdown was within ±10 minutes of our prediction." /></th>
              <th className="numeric">&lt;15 min<HeaderHint text="Share of flights whose actual touchdown was within ±15 minutes of our prediction." /></th>
              <th className="numeric">80% band<HeaderHint text="Calibration, not accuracy: the share of flights that landed inside our own p10–p90 range. The target is 80% — higher means the range is too wide, lower too narrow. The range narrows in calm traffic and is not centred, so it can be tighter than ±5 min; this can read below '<5 min' without either being wrong." /></th>
            </tr>
          </thead>
          <tbody>
            {ringRows.map(r => {
              const handover = r.ringNm === HANDOVER_NM;
              return (
                <tr key={r.ringNm} className={r.flights === 0 ? 'pred-accuracy-empty-row' : ''}>
                  <td>
                    {r.ringNm} NM
                    {handover && (
                      <span className="pred-accuracy-boundary"> · model handover</span>
                    )}
                  </td>
                  {/* The handover ring can never be measured — no single model's leg spans it —
                      so it says why rather than showing ten em-dashes and looking broken. */}
                  {handover && r.flights === 0 ? (
                    <td className="pred-accuracy-handover-note" colSpan={10}>
                      the trained model stops here and the long-range estimator starts, so no
                      one model's track spans this ring — read 150 NM and 300 NM either side
                    </td>
                  ) : (
                    <>
                      <td className="numeric">{r.flights ? fmtInt(r.flights) : EMPTY}</td>
                      <td className="numeric strong">{r.maeMinutes != null ? fmtNum(r.maeMinutes, 1) : EMPTY}</td>
                      <td className="numeric strong">{fmtSigned(r.biasMinutes)}</td>
                      <td className="numeric strong">{r.p50Minutes != null ? fmtNum(r.p50Minutes, 1) : EMPTY}</td>
                      <td className="numeric strong">{r.p90Minutes != null ? fmtNum(r.p90Minutes, 1) : EMPTY}</td>
                      <td className="numeric strong">{r.p95Minutes != null ? fmtNum(r.p95Minutes, 1) : EMPTY}</td>
                      <td className="numeric strong">{r.within5 != null ? fmtPercent(r.within5 * 100, 0) : EMPTY}</td>
                      <td className="numeric strong">{r.within10 != null ? fmtPercent(r.within10 * 100, 0) : EMPTY}</td>
                      <td className="numeric strong">{r.within15 != null ? fmtPercent(r.within15 * 100, 0) : EMPTY}</td>
                      <td className="numeric strong">{r.coverage80 != null ? fmtPercent(r.coverage80 * 100, 0) : EMPTY}</td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {!loadingWindow && scoredRings.length === 0 && (
        <div className="pred-accuracy-hint">
          No flights have been scored in this window yet. A flight is scored once it has landed
          and its touchdown time is known, so a short window at a quiet hour can legitimately be
          empty.
        </div>
      )}

      <div className="pred-accuracy-controls">
        <span className="pred-accuracy-label">Against the published estimate</span>
        <div className="predictions-tabs pred-accuracy-pills">
          {BENCH_WINDOWS.map(w => (
            <button
              key={w.hours}
              className={`predictions-tab${benchHours === w.hours ? ' active' : ''}`}
              onClick={() => setBenchHours(w.hours)}
            >{w.label}</button>
          ))}
        </div>
        <span className="pred-accuracy-note">
          {bench && bench.flights ? `${fmtInt(bench.flights)} flights` : ''}
        </span>
      </div>

      <p className="pred-accuracy-lead">
        Our arrival time against the airline schedule provider's, judged at the same instant.
        Each estimate of theirs is scored as of the moment we fetched it, and ours is rewound to
        that same moment — without that, their half-hour-old number would be compared against our
        twenty-second-old one. Each side is scored against its own truth: theirs is on-block,
        ours is touchdown{bench && bench.meanTaxiInMinutes != null
          ? `, currently ${fmtNum(bench.meanTaxiInMinutes, 1)} min apart on average`
          : ''}, so the taxi-in cancels rather than counting as our lead.
      </p>
      <div className="pred-accuracy-table-wrap">
        <table className="predictions-table pred-accuracy-table">
          <thead>
            <tr>
              <th>Horizon<HeaderHint text="How long before the arrival the two estimates were made." /></th>
              <th className="numeric">Compared<HeaderHint text="Estimates where both sides had a current answer. The averages are over these only." /></th>
              <th className="numeric">Our MAE<HeaderHint text="Our mean absolute error in minutes, measured against our observed touchdown." /></th>
              <th className="numeric">Theirs<HeaderHint text="The published estimate's mean absolute error in minutes, measured against the airline's own on-block (at-gate) time." /></th>
              <th className="numeric">Our p90<HeaderHint text="90th percentile of our absolute error: 9 in 10 of our estimates were within this." /></th>
              <th className="numeric">Theirs<HeaderHint text="90th percentile of the published estimate's absolute error." /></th>
              <th className="numeric">Our bias<HeaderHint text="Our signed mean error. Positive = we predicted too late." /></th>
              <th className="numeric">Theirs<HeaderHint text="The published estimate's signed mean error. Positive = they predicted too late." /></th>
              <th className="numeric">We led<HeaderHint text="Share of compared estimates where our error was the smaller one." /></th>
              <th className="numeric">We were silent<HeaderHint text="Estimates they published while we had not predicted yet. Not a win for either side — it is the share of the problem we were not attempting." /></th>
            </tr>
          </thead>
          <tbody>
            {benchRows.map(h => (
              <tr key={h.label} className={h.matched === 0 ? 'pred-accuracy-empty-row' : ''}>
                <td>{h.label}</td>
                <td className="numeric">{h.matched ? fmtInt(h.matched) : EMPTY}</td>
                {/* The smaller error is bolded rather than coloured: Mono carries no hue, so a
                    green/red pair would read as two identical cells there. */}
                <td className={`numeric${leads(h.ourMaeMinutes, h.theirMaeMinutes) ? ' strong' : ''}`}>
                  {h.ourMaeMinutes != null ? fmtNum(h.ourMaeMinutes, 1) : EMPTY}
                </td>
                <td className={`numeric${leads(h.theirMaeMinutes, h.ourMaeMinutes) ? ' strong' : ''}`}>
                  {h.theirMaeMinutes != null ? fmtNum(h.theirMaeMinutes, 1) : EMPTY}
                </td>
                <td className={`numeric${leads(h.ourP90Minutes, h.theirP90Minutes) ? ' strong' : ''}`}>
                  {h.ourP90Minutes != null ? fmtNum(h.ourP90Minutes, 1) : EMPTY}
                </td>
                <td className={`numeric${leads(h.theirP90Minutes, h.ourP90Minutes) ? ' strong' : ''}`}>
                  {h.theirP90Minutes != null ? fmtNum(h.theirP90Minutes, 1) : EMPTY}
                </td>
                <td className="numeric">{h.ourBiasMinutes != null ? fmtSigned(h.ourBiasMinutes) : EMPTY}</td>
                <td className="numeric">{h.theirBiasMinutes != null ? fmtSigned(h.theirBiasMinutes) : EMPTY}</td>
                <td className="numeric">{h.ourWinRate != null ? fmtPercent(h.ourWinRate * 100, 0) : EMPTY}</td>
                <td className="numeric">{h.weWereSilent ? fmtInt(h.weWereSilent) : EMPTY}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!loadingBench && benchMatched === 0 && (
        <div className="pred-accuracy-hint">
          {airportIcao === BENCHMARKED_AIRPORT ? (
            <>
              Nothing compared yet. The provider overwrites its estimate with the actual time on
              landing, so an estimate only counts if we captured it while the aircraft was still
              airborne — which means this table fills going forward and cannot be backfilled.
            </>
          ) : (
            <>
              Not measured at this airport. The comparison only runs where the provider's schedule
              refreshes often enough to be an estimate rather than an echo — every 30 minutes at
              {' '}{BENCHMARKED_AIRPORT}, against every three hours here, which would measure their
              refresh interval instead of their accuracy.
            </>
          )}
        </div>
      )}

      <DepartureAccuracyPanel airportIcao={airportIcao} />

      <div className="pred-accuracy-controls">
        <span className="pred-accuracy-label">History</span>
        <div className="predictions-tabs pred-accuracy-pills">
          {TREND_RANGES.map(t => (
            <button
              key={t.days}
              className={`predictions-tab${trendDays === t.days ? ' active' : ''}`}
              onClick={() => setTrendDays(t.days)}
            >{t.label}</button>
          ))}
        </div>
      </div>

      <div className="chart-grid">
        <ChartCard
          title="Close-in accuracy, 20–200 NM"
          subtitle="mean absolute error per day · one observation per flight per ring"
          description={`How wrong the ETA was, in minutes, for aircraft inside ${HANDOVER_NM} NM —
            the range the trained model serves. Each line is one distance ring and each point is
            one day's flights averaged. Lines should sit low and, on any given day, fan out in
            ring order: an aircraft 100 NM out is genuinely harder to call than one at 20 NM. A
            day where the rings converge or cross is worth opening — it usually means disruption
            (holding, a runway change) hurting the close-in rings that are normally easiest.
            A ring-day scored on fewer than ${MIN_TREND_FLIGHTS} flights is left off.`}
          span="full"
          loading={loadingHistory}
          empty={closeInSeries.length === 0}
          emptyText="Nothing scored inside 200 NM in this period yet"
          legend={closeInSeries.map(s => ({ name: s.name, slot: s.slot }))}
        >
          <LineChart series={closeInSeries} unit="min" axisLabel="MAE, minutes" height={260}
                     format={(v) => fmtNum(v, 1)} />
        </ChartCard>

        <ChartCard
          title="Long-range accuracy, beyond 200 NM"
          subtitle="mean absolute error per day · analytic estimator, not the ML model"
          description={`The same measurement for aircraft the trained model never sees. Beyond
            ${HANDOVER_NM} NM an analytic estimator runs instead, corrected by the regime offset
            charted below, so this is a different predictor being scored — which is why it has
            its own axis rather than sharing one with the close-in rings and being flattened
            against them. Expect both larger errors and a much thinner cohort: only the France,
            Pyrenees and Porto receivers reach this far, so a handful of flights can move a day;
            a ring-day scored on fewer than ${MIN_TREND_FLIGHTS} flights is left off.`}
          span="full"
          loading={loadingHistory}
          empty={longRangeSeries.length === 0}
          emptyText="Nothing scored beyond 200 NM in this period yet"
          legend={longRangeSeries.map(s => ({ name: s.name, slot: s.slot }))}
        >
          <LineChart series={longRangeSeries} unit="min" axisLabel="MAE, minutes" height={240}
                     format={(v) => fmtNum(v, 1)} />
        </ChartCard>

        <ChartCard
          title="Long-range regime correction"
          subtitle={`minutes subtracted from the raw ETA beyond ${HANDOVER_NM} NM · clamped to ±${clamp}`}
          note={neutralCount > 0
            ? `${neutralCount} of ${regime.samples.length} refreshes served no correction (fewer than four landed flights in the trailing window)`
            : null}
          description={`Most long-range error is one constant per flight, set by the weather of
            the day — enroute wind plus how backed-up Dublin is. Rather than forecast that, the
            estimator measures it: every five minutes it takes the residual of long-range flights
            that have already landed and subtracts the trimmed average from new predictions. This
            is that offset over time. Positive means the raw estimator was running late and is
            being pulled earlier. Sustained excursions towards the ±${clamp} clamp mean the
            trailing window disagrees with the model more than the correction is allowed to
            express; a flat zero means no correction was served at all.`}
          span="full"
          loading={loadingHistory}
          empty={regimeSeries.length === 0}
          emptyText="No regime snapshots recorded yet"
          legend={[{ name: 'offset applied', slot: 1 }]}
        >
          <LineChart series={regimeSeries} unit="min" axisLabel="offset, minutes" height={220}
                     yMin={-clamp} yMax={clamp}
                     format={(v) => fmtSigned(v)} />
        </ChartCard>

        <ChartCard
          title="Interval spread scale"
          subtitle="multiplier on the published p10–p90 half-width; 1.0 is a typical regime"
          description={`How much the published uncertainty band is being widened or narrowed
            relative to a normal day, from the same trailing window as the offset above. Above
            1.0 the recent residuals were unusually scattered and the arrival-outlook bar shown
            to users widens to match; below 1.0 the day is calmer than usual and it tightens.
            It is a companion to the offset, not a duplicate: the offset moves the estimate, this
            moves the confidence around it.`}
          loading={loadingHistory}
          empty={spreadSeries.length === 0}
          legend={[{ name: 'spread scale', slot: 2 }]}
        >
          <LineChart series={spreadSeries} height={200} axisLabel="× typical half-width"
                     format={(v) => fmtNum(v, 2)} />
        </ChartCard>

        <ChartCard
          title="Flights the correction rests on"
          subtitle="landed long-range flights inside the trailing window"
          description={`How much evidence the two charts above are standing on at each refresh.
            Fewer than four landed long-range flights and no correction is served at all, which
            is what the neutral periods in the offset chart are. A low count does not mean the
            correction is wrong, but it does mean one unusual flight can move it — read the
            offset against this line, not on its own.`}
          loading={loadingHistory}
          empty={flightsSeries.length === 0}
          legend={[{ name: 'landed flights in window', slot: 3 }]}
        >
          <LineChart series={flightsSeries} height={200} axisLabel="flights" format={fmtInt} />
        </ChartCard>
      </div>
    </div>
  );
}

export default PredictionAccuracy;
